import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import Select from "react-select";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import { proxyAPI } from "../proxyAPI";
import InputLabel from "../Builder/nodes/Logic/Inputs/InputLabel";
import HelpButton from "../HelpContent/HelpButton";

export default function WeatherAlertConfiguration({
  weatherAlertData,
  setWeatherAlertData,
  errors: parentErrors,
}) {
  const [loading, setLoading] = useState(false);
  const [identifier, setIdentifier] = useState({});
  const [countries, setCountries] = useState([
    { value: "GB", label: "United Kingdom", selected: true },
  ]); // later on fetch from api instead
  const selectedCountry = countries.find(
    (c) => c.value.toUpperCase() === weatherAlertData?.CountryIso?.toUpperCase()
  );
  const [geoUnitTypes, setGeoUnitTypes] = useState([
    { value: "country", label: "Whole Country" },
    { value: "region", label: "Region" },
    { value: "county", label: "County" },
  ]);
  const [geoUnitGroups, setGeoUnitGroups] = useState([
    { value: "country", label: "Country" },
    { value: "region", label: "Region" },
    { value: "county", label: "County" },
  ]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedCountyRegion, setSelectedCountyRegion] = useState(null);
  const [selectedGroupBy, setSelectedGroupBy] = useState(null);
  const [countyOrGroupPage, setCountyOrGroupPage] = useState(0);
  const [countyOrGroupOptions, setCountyOrGroupOptions] = useState([]);
  const totalCountyOrGroup = useRef(0);
  const [countyOrGroupSearch, setCountyOrGroupSearch] = useState("");
  const [isCountyRegionLoading, setIsCountyRegionLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Alert form state
  const [alertData, setAlertData] = useState({
    ID: 0,
    ShapeId: 0,
    GroupName: "",
    Active: false,
    Priority: 1,
    Description: "",
    Stop: false,
    CountryIso: "",
    GeoUnitType: "",
    GeoUnitGroup: "",
  });

  const getCountiesOrGroups = async (search, page) => {
    try {
      let searchType = "groups";

      if (selectedArea?.value == "county") {
        searchType = "shapes";
      }

      let queryParams = `search=${search}`;
      if (selectedCountry && selectedCountry?.value !== "") {
        queryParams += `&country=${selectedCountry?.value}`;
      }

      queryParams += `&page=${page}&perPage=50`;

      const data = await proxyAPI({
        prefix: "logic/workflows/api",
        url: `geo/${searchType}?${queryParams}`,
        setLoading,
      });

      let options = [];
      if (searchType === "shapes" && data.shapes) {
        options = data.shapes.map((gs) => ({
          value: gs.ID,
          label: gs.Name,
        }));
      } else if (searchType == "groups" && data.groups) {
        options = data.groups.map((group) => ({
          value: group.Item,
          label: group.Item,
        }));
      }
      if (page == 0) {
        setCountyOrGroupOptions([]);
      }

      setCountyOrGroupOptions((prev) => {
        const updatedOptions = [...prev, ...options];
        return updatedOptions;
      });

      totalCountyOrGroup.current = data.total;

      if (
        selectedCountyRegion?.value &&
        search == "" &&
        !options.some((opt) => opt.value === selectedCountyRegion.value)
      ) {
        options = [identifier, ...options];
      }

      return options;
    } catch (error) {
      console.error("Error fetching counties/group", error);
      return [];
    }
  };

  // Error state
  const [errors, setErrors] = useState({});

  const handleAlertChange = (field, value) => {
    const updatedData = {
      ...alertData,
      [field]: value,
    };
    setAlertData(updatedData);

    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
      });
    }
  };

  useEffect(() => {
    const loadOptions = async () => {
      setIsCountyRegionLoading(true);
      try {
        const newOptions = await getCountiesOrGroups(
          debouncedSearch,
          countyOrGroupPage
        );

        // Replace options if it's page 0, otherwise append
        if (countyOrGroupPage === 0) {
          setCountyOrGroupOptions(newOptions);
        } else {
          setCountyOrGroupOptions((prev) => {
            // Create a map to check for duplicates
            const uniqueMap = new Map(prev.map((item) => [item.value, item]));

            // Add new options if they don't already exist
            newOptions.forEach((option) => {
              if (!uniqueMap.has(option.value)) {
                uniqueMap.set(option.value, option);
              }
            });

            // Convert map back to array
            return Array.from(uniqueMap.values());
          });
        }
      } catch (error) {
        console.error("Error loading options:", error);
      } finally {
        setIsCountyRegionLoading(false);
      }
    };

    loadOptions();
  }, [
    debouncedSearch,
    countyOrGroupPage,
    selectedArea?.value,
    selectedCountry?.value,
  ]);

  useEffect(() => {
    if (!setWeatherAlertData) return;
    setWeatherAlertData({
      ...weatherAlertData,
      ...alertData,
      CountryIso: selectedCountry?.value,
      GeoUnitType: selectedArea?.value,
      GeoUnitGroup: selectedGroupBy?.value || "",
      ShapeId:
        selectedArea?.value === "county"
          ? parseInt(selectedCountyRegion?.value, 10)
          : 0,
      GroupName:
        selectedArea?.value === "region" ? selectedCountyRegion?.value : "",
    });
  }, [
    alertData,
    selectedCountyRegion,
    selectedCountry,
    selectedArea,
    selectedGroupBy,
  ]);

  useEffect(() => {
    if (!weatherAlertData) return;
    setAlertData(weatherAlertData);
    setSelectedArea(() =>
      geoUnitTypes.find((gt) => gt.value == weatherAlertData.GeoUnitType)
    );
    setSelectedGroupBy(() =>
      geoUnitGroups.find((gg) => gg.value == weatherAlertData.GeoUnitGroup)
    );

    (async () => {
      const url = `/geo/${
        weatherAlertData.GroupName === ""
          ? `shapes?id=${weatherAlertData.ShapeId}`
          : `groups?search=${weatherAlertData.GroupName}`
      }`;

      proxyAPI({
        prefix: "logic/workflows/api",
        url,
        setLoading,
      })
        .then((shapeOrGroup) => {
          if (shapeOrGroup) {
            setSelectedCountyRegion(null);
            if (weatherAlertData.ShapeId > 0) {
              setSelectedCountyRegion({
                value: shapeOrGroup.shapes[0]?.ID,
                label: shapeOrGroup.shapes[0]?.Name,
              });
            } else if (weatherAlertData.GroupName !== "") {
              setSelectedCountyRegion({
                value: shapeOrGroup.groups[0]?.Item,
                label: shapeOrGroup.groups[0]?.Item,
              });
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching shape or county data:", error);
        });
    })();
  }, []);

  return (
    <div className="my-3 rounded-3">
      <div className="row">
        <div className="col-12">
          <h4>
            Alert Configuration{" "}
            <HelpButton helpKey="weather-alert-configuration" />
          </h4>
          {errors.conditions && (
            <div className="text-danger">{errors.conditions}</div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-md-3">
          <InputLabel>
            Select Country
            <HelpButton helpKey="weather-alert-country" />
          </InputLabel>
          <Select
            isClearable
            value={selectedCountry}
            onChange={(v) => {
              setSelectedGroupBy(null);
              setSelectedCountyRegion(null);
              setSelectedArea(null);
              setWeatherAlertData({
                ...weatherAlertData,
                CountryIso: v?.value,
              });
            }}
            options={countries}
          />
          {parentErrors && parentErrors["Monitor.WeatherAlert.CountryIso"] && (
            <div className="text-danger">
              {parentErrors["Monitor.WeatherAlert.CountryIso"]}
            </div>
          )}
        </div>
        <div className="col-md-3" hidden={!selectedCountry}>
          <InputLabel>
            Select Area
            <HelpButton helpKey="weather-alert-area" />
          </InputLabel>
          <Select
            isClearable
            isDisabled={!selectedCountry}
            value={selectedArea}
            onChange={(v) => {
              setSelectedGroupBy(null);
              setSelectedCountyRegion(null);
              setCountyOrGroupOptions([]);
              setCountyOrGroupPage(0);
              setSelectedArea(v);
            }}
            options={geoUnitTypes}
          />
          {parentErrors && parentErrors["Monitor.WeatherAlert.GeoUnitType"] && (
            <div className="text-danger">
              {parentErrors["Monitor.WeatherAlert.GeoUnitType"]}
            </div>
          )}
        </div>
        <div
          className="col-md-3"
          hidden={
            !selectedCountry ||
            !selectedArea ||
            selectedArea?.value === "country"
          }
        >
          <InputLabel>
            Select {selectedArea?.value}
            <HelpButton helpKey={`weather-alert-${selectedArea?.value}`} />
          </InputLabel>
          <Select
            key={`${selectedArea?.value}-select`}
            value={selectedCountyRegion}
            onChange={(v) => {
              setSelectedCountyRegion(v);
              console.log("Selected County/Region:", v);
            }}
            options={countyOrGroupOptions}
            isLoading={isCountyRegionLoading}
            isClearable
            isSearchable
            onInputChange={(value) => {
              setCountyOrGroupSearch(value);
            }}
            filterOption={(option, inputValue) => true}
            onMenuScrollToBottom={() => {
              if (countyOrGroupOptions.length < totalCountyOrGroup.current) {
                setCountyOrGroupPage((prev) => prev + 1);
              }
            }}
          />
          {parentErrors &&
            parentErrors["Monitor.WeatherAlert.selectedCountyRegion"] && (
              <div className="text-danger">
                {parentErrors["Monitor.WeatherAlert.selectedCountyRegion"]}
              </div>
            )}
        </div>
        <div
          className="col-md-3 d-none"
          hidden={selectedArea?.value === "county"}
        >
          <InputLabel>
            Group By
            <HelpButton helpKey="weather-alert-group-by" />
          </InputLabel>
          <Select
            isClearable
            value={selectedGroupBy}
            onChange={(v) => {
              setSelectedGroupBy(v);
            }}
            filterOption={(opt) => {
              if (selectedArea?.value === "region") {
                return opt?.value !== "country";
              }
              return true;
            }}
            options={geoUnitGroups}
          />
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-12">
          <Input
            placeholder="This content will be used in alert communications."
            inputType="textArea"
            label="Alert Description"
            value={[alertData.Description]}
            onChange={(values) => handleAlertChange("Description", values[0])}
            options={[{ value: "", label: "" }]}
          />
          {parentErrors && parentErrors["Monitor.WeatherAlert.description"] && (
            <div className="text-danger">
              {parentErrors["Monitor.WeatherAlert.description"]}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
